import { type IContextualMenuItem } from '@fluentui/react';
import { Button } from '@h2oai/ui-kit';
import React from 'react';

import { useQueryMyWorkspaces } from '../../../authz/authz.hooks';
import { type User, userName } from '../../../authz/gen/ai/h2o/user/v1/user_pb';
import { workspaceName } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useUsers } from '../../../authz/providers/UsersProvider';
import SelectableItemsCallout from '../../../components/SelectableItemCallout/SelectableItemsCallout';
import { useInfiniteQueryFetchAllPages } from '../../../services/hooks';
import { WidgetListFilter } from '../WidgetListFilter';

export type ModelsFilterProps = {
  className?: string;
  onCreatorChange: (creator?: string) => void;
  onResetFilter?: () => void;
  onWorkspaceChange: (workspace?: string) => void;
  selectedCreator?: string;
  selectedWorkspace?: string;
};

export const ModelsFilter = (props: ModelsFilterProps) => {
  const { onCreatorChange, onResetFilter, onWorkspaceChange, selectedCreator, selectedWorkspace } = props;

  const { users, usersAndGroupsNameMap } = useUsers();
  const { creator, creatorItems } = React.useMemo<{
    creator?: IContextualMenuItem;
    creatorItems: IContextualMenuItem[];
  }>(() => {
    const creatorItems = [];
    let creator = undefined;
    if (users?.length) {
      for (const user of users) {
        const userId = userName.parse(user.name!).user;
        const checked = selectedCreator === userId;
        const creatorItem = {
          canCheck: true,
          checked,
          key: userId,
          onClick: () => {
            onCreatorChange(checked ? undefined : userId);
          },
          text: user.displayName! || user.loginPrincipal!,
        };
        creatorItems.push(creatorItem);
        if (checked) creator = creatorItem;
      }
    }
    return { creatorItems, creator };
  }, [users, selectedCreator]);

  const workspaceQuery = useQueryMyWorkspaces(
    {},
    {
      select: (data) => {
        const flattenedWorkspaces = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: flattenedWorkspaces, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(workspaceQuery);
  const workspaces = workspaceQuery.data?.pages;
  const { workspace, workspaceItems } = React.useMemo<{
    workspace?: IContextualMenuItem;
    workspaceItems: IContextualMenuItem[];
  }>(() => {
    const workspaceItems = [];
    let workspace = undefined;
    if (workspaces?.length) {
      for (const ws of workspaces) {
        const workspaceId = workspaceName.parse(ws.name!).workspace;
        const checked = selectedWorkspace === workspaceId;
        const personalWorkspaceGroupOrUser =
          ws.personalWorkspaceSubject && usersAndGroupsNameMap?.[ws.personalWorkspaceSubject];
        const personalWorkspaceText = !ws.personalWorkspaceSubject
          ? undefined
          : !personalWorkspaceGroupOrUser
          ? ws.personalWorkspaceSubject
          : personalWorkspaceGroupOrUser.displayName ||
            (personalWorkspaceGroupOrUser as User).loginPrincipal ||
            personalWorkspaceGroupOrUser.name;
        const workspaceItem = {
          canCheck: true,
          checked,
          key: workspaceId,
          onClick: () => {
            onWorkspaceChange(checked ? undefined : workspaceId);
          },
          text: ws.displayName! || workspaceId,
          secondaryText: personalWorkspaceText,
        };
        workspaceItems.push(workspaceItem);
        if (checked) workspace = workspaceItem;
      }
    }
    return { workspace, workspaceItems };
  }, [selectedWorkspace, usersAndGroupsNameMap, workspaces]);
  return (
    <WidgetListFilter className={props.className}>
      <SelectableItemsCallout
        buttonText={
          (
            <>
              Creator
              {creator && (
                <>
                  : <em>{creator.text}</em>
                  {`\xA0`}
                </>
              )}
            </>
          ) as any // ReactNode is proven to work, but fluentui narrows it to string only
        }
        menuItems={creatorItems}
        searchBoxPlaceholderText="Search creators"
        emptyItemsText="No creators to select from."
        calloutNoItemsText="No creators found"
        calloutNoItemsIconName="SearchIssue"
        minimal
        openContextMenuIfNoItems
      />
      <SelectableItemsCallout
        buttonText={
          (
            <>
              Workspace
              {workspace && (
                <>
                  :{' '}
                  <em>
                    {workspace.text}
                    {workspace.secondaryText && ` (${workspace.secondaryText})`}
                  </em>
                </>
              )}
            </>
          ) as any // ReactNode is proven to work, but fluentui narrows it to string only
        }
        menuItems={workspaceItems}
        searchBoxPlaceholderText="Search workspaces"
        emptyItemsText="No workspaces to select from."
        calloutNoItemsText="No workspaces found"
        calloutNoItemsIconName="SearchIssue"
        minimal
        openContextMenuIfNoItems
        styles={{
          contextualMenu: { width: `unset` },
        }}
      />

      {onResetFilter && (
        <Button
          text="Clear filters"
          onClick={onResetFilter}
          iconName="Cancel"
          styles={{
            root: { width: 'auto', border: 'none' },
            icon: { fontSize: 12 },
          }}
          buttonContainerStyles={{ root: { textAlign: 'center' } }}
        />
      )}
    </WidgetListFilter>
  );
};
